import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, ArrowUpRight, Database } from "lucide-react";
import { getCommodity, groups, inGroup, GRADE_UNIT_OPTIONS } from "@/lib/commodities";
import useAutoFill from "@/hooks/useAutoFill";
import AutoFilledHint from "@/components/AutoFilledHint";

export default function Stage1ProjectSetup({ projectId, onCompleted }) {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [form, setForm] = useState({ name: "", commodity: "", coord_system: "", unit_system: "metric", commodity_price: 0, notes: "", grade_unit_override: "" });
  const [saving, setSaving] = useState(false);
  // Auto-fill hook — resolves commodity + CRS from actual uploaded data
  const auto = useAutoFill(projectId);
  const commodityF = auto.field("commodity");
  const crsF = auto.field("coordinate_system");

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}`).then(({ data }) => {
      setProject(data);
      setForm({
        name: data.name || "",
        commodity: data.commodity || "",
        coord_system: data.coord_system || data.coordinate_system || "",
        unit_system: data.unit_system || "metric",
        commodity_price: data.commodity_price ?? 0,
        notes: data.notes || "",
        grade_unit_override: data.grade_unit_override || "",
      });
    }).catch(() => {});
  }, [projectId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/projects/${projectId}`, { ...form, grade_unit_override: form.grade_unit_override || null });
      // Explicitly mark Stage 1 complete once required fields exist
      if (form.commodity && form.coord_system) {
        await api.post(`/projects/${projectId}/pipeline-state/1/complete`, {
          output_summary: {
            commodity: form.commodity, coord_system: form.coord_system,
            unit_system: form.unit_system,
          },
        });
      }
      toast.success("Project setup saved");
      onCompleted?.();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="stage1-content">
      {/* Enterprise Master Project Database — the new source of truth. Stage 1 keeps its
          quick-edit form for pipeline flow, but the full 26-section editor lives here. */}
      <div className="rounded-lg border border-[#4CAF50]/40 bg-[#4CAF50]/5 p-4 flex items-center justify-between gap-3"
           data-testid="stage1-master-cta">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-[#4CAF50] text-white grid place-items-center shrink-0">
            <Database className="h-5 w-5"/>
          </div>
          <div>
            <div className="text-[13.5px] font-semibold text-neutral-900">
              Enterprise Project Setup — Master Project Database
            </div>
            <div className="text-[11.5px] text-neutral-600 mt-0.5 max-w-2xl">
              Open the full 26-section master editor: Basic Info · Location · Coordinate System ·
              Commodity · Units · Currency · Licence · Report · Timeline · Personnel · Team ·
              Files · Photos · Maps · Contacts · Custom Fields · Audit Trail. Every report and
              module auto-populates from here.
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate(`/projects/${projectId}/master`)}
          data-testid="stage1-open-master"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-[#4CAF50] hover:bg-[#43A047] text-white text-[12px] font-semibold shadow-sm shrink-0">
          Open Master Editor <ArrowUpRight className="h-3.5 w-3.5"/>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <Field label="Project name">
          <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}
            className="w-full bg-neutral-50 border border-neutral-200 rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#4CAF50]/50"
            data-testid="stage1-name" />
        </Field>
        <Field label="Commodity">
          <select value={form.commodity} onChange={(e)=>setForm({...form,commodity:e.target.value})}
            className="w-full bg-neutral-50 border border-neutral-200 rounded px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-[#4CAF50]/50"
            data-testid="stage1-commodity">
            <option value="">Select commodity…</option>
            {groups().map((g) => (
              <optgroup key={g} label={g}>
                {inGroup(g).map((c) => {
                  // Precompute label as a single string. The <option> element
                  // does not accept nested elements — some dev instrumentation
                  // wraps JSX expressions in <span>s which then triggers a
                  // hydration error ("<span> cannot be a child of <option>").
                  const label = `${c.code} · ${c.name} (${c.grade_unit})`;
                  return (
                    <option key={c.code} value={c.code}>{label}</option>
                  );
                })}
              </optgroup>
            ))}
          </select>
          {form.commodity && (
            <div className="text-[10.5px] text-neutral-500 mt-1">
              {getCommodity(form.commodity).hint} · typical unit{" "}
              <span className="text-[#4CAF50] font-mono">{getCommodity(form.commodity).grade_unit}</span>
            </div>
          )}
          {commodityF && commodityF.value !== form.commodity && (
            <AutoFilledHint
              reason={`suggests ${commodityF.value} · ${commodityF.reason}`}
              source={commodityF.source}
              onAccept={() => setForm((f) => ({ ...f, commodity: commodityF.value }))}
            />
          )}
        </Field>
        <Field label="Coordinate system / datum">
          <input value={form.coord_system} onChange={(e)=>setForm({...form,coord_system:e.target.value})}
            placeholder="e.g. UTM Zone 43N · WGS84"
            className="w-full bg-neutral-50 border border-neutral-200 rounded px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-[#4CAF50]/50"
            data-testid="stage1-coord" />
          {crsF && crsF.value !== form.coord_system && (
            <AutoFilledHint
              reason={`suggests "${crsF.value}" · ${crsF.reason}`}
              source={crsF.source}
              onAccept={() => setForm((f) => ({ ...f, coord_system: crsF.value }))}
            />
          )}
        </Field>
        <Field label="Unit system">
          <select value={form.unit_system} onChange={(e)=>setForm({...form,unit_system:e.target.value})}
            className="w-full bg-neutral-50 border border-neutral-200 rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#4CAF50]/50"
            data-testid="stage1-units">
            <option value="metric">Metric (m · kg · t)</option>
            <option value="imperial">Imperial (ft · lb · st)</option>
          </select>
        </Field>
        <Field label="Grade unit (manual override)">
          <select value={form.grade_unit_override || ""} onChange={(e)=>setForm({...form,grade_unit_override:e.target.value})}
            className="w-full bg-neutral-50 border border-neutral-200 rounded px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-[#4CAF50]/50"
            data-testid="stage1-grade-unit-override">
            <option value="">
              {`Use commodity default${form.commodity ? ` (${getCommodity(form.commodity).grade_unit})` : ""}`}
            </option>
            {GRADE_UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <div className="text-[10.5px] text-neutral-500 mt-1">
            Ranges, sliders &amp; colour scales still auto-range from real assay data — override changes the LABEL only.
          </div>
        </Field>
        <Field label="Commodity price assumption (USD)">
          <input type="number" value={form.commodity_price} onChange={(e)=>setForm({...form,commodity_price:parseFloat(e.target.value)||0})}
            className="w-full bg-neutral-50 border border-neutral-200 rounded px-3 py-2 text-[13px] font-mono tabular-nums focus:outline-none focus:border-[#4CAF50]/50"
            data-testid="stage1-price" />
        </Field>
        <Field label="Tenement boundary">
          <div className="text-[11.5px] text-neutral-500 p-2.5 border border-dashed border-neutral-300 rounded">
            GIS upload (shapefile / GeoJSON / DXF) — placeholder; Session E wires the actual GIS layer picker
          </div>
        </Field>
      </div>
      <div className="flex items-center justify-end gap-3">
        <button onClick={save} disabled={saving || !form.commodity || !form.coord_system}
          className="text-[12px] px-4 py-2 bg-[#4CAF50] hover:bg-[#43A047] disabled:bg-neutral-100 disabled:text-neutral-400 rounded text-white font-medium flex items-center gap-1.5 transition"
          data-testid="stage1-save">
          <Save className="h-3.5 w-3.5"/> Save & mark complete
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500">{label}</div>
      {children}
    </label>
  );
}
